import React, { useState, useCallback } from 'react'
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteChangeReason,
  AutocompleteChangeDetails
} from '@material-ui/lab/Autocomplete'
import { TextFieldProps } from '@material-ui/core/TextField'
import { Field, FieldProps } from 'formik'

import { TextField, Autocomplete } from './styled'

import { t } from 'i18n'
import withInlineWrapper from './withInlineWrapper'
import withTableWrapper from './withTableWrapper'
import { map, reduce, find } from 'lodash'

export interface SelectProps<T extends { id: string }> {
  name: string
  AutocompleteProps: Omit<AutocompleteProps<T, undefined, undefined, undefined>, 'renderInput' | 'multiple' | 'disableClearable'> & {
    multiple?: boolean
    disableClearable?: boolean
    renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode
  }
  TextFieldProps?: TextFieldProps
}

function SelectField<T extends { id: string }>({
  name,
  AutocompleteProps: { onChange: afterChange, options, ...AutocompleteProps },
  TextFieldProps
}: React.PropsWithChildren<SelectProps<T>>): JSX.Element {
  const [inputValue, setInputValue] = useState('')

  const handleInputChange = (event: React.ChangeEvent, value: string): void => setInputValue(value)

  const injectValue = useCallback((value: T | T[] | null, options: T[]): T[] => {
    const values: T[] = value ? (Array.isArray(value) ? value : [value]) : []

    return reduce(
      values,
      (current, value) => {
        if (!find(current, ({ id }) => id === value.id)) {
          return [value, ...current]
        } else {
          return current
        }
      },
      options
    )
  }, [])

  return (
    <Field name={name}>
      {({ field: { value }, meta: { error }, form: { setFieldValue } }: FieldProps) => (
        <Autocomplete
          fullWidth
          options={injectValue(value, options)}
          getOptionSelected={(option: { id: string }, value: { id: string }) => option?.id === value?.id}
          value={value}
          onChange={(
            event: React.ChangeEvent<{}>,
            value: T,
            reason: AutocompleteChangeReason,
            details?: AutocompleteChangeDetails<T> | undefined
          ) => {
            setFieldValue(name, value)

            if (typeof afterChange === 'function') {
              afterChange(event, value, reason, details)
            }
          }}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          noOptionsText={t('type_to_search')}
          clearText={t('clear')}
          openText={t('open')}
          filterSelectedOptions
          autoHighlight
          renderInput={(params) => {
            const { InputProps, ...otherParams } = params

            return (
              <TextField
                margin='dense'
                variant='outlined'
                name={name}
                error={Boolean(error)}
                helperText={error}
                {...otherParams}
                InputProps={{
                  notched: false,
                  ...InputProps
                }}
                InputLabelProps={{
                  shrink: true
                }}
                {...TextFieldProps}
              />
            )
          }}
          {...AutocompleteProps}
        />
      )}
    </Field>
  )
}

const getFieldTextValue = <T extends { id: string }>(value: T | T[], props: SelectProps<T>): string => {
  const values: T[] = value ? (Array.isArray(value) ? value : [value]) : []

  if (values.length > 0) {
    if (props.AutocompleteProps?.getOptionLabel) {
      return map(values, props.AutocompleteProps.getOptionLabel).join(', ')
    } else {
      return values.join(', ')
    }
  } else {
    return props.TextFieldProps?.placeholder || '-'
  }
}

export const SelectInlineField = withInlineWrapper(SelectField, getFieldTextValue, { autoFocus: false })

export const TableSelectInlineField = withTableWrapper(SelectInlineField, (props) => props.TextFieldProps)

export default SelectField
